import { useState, useEffect, useRef, useCallback } from "react";
import { ProcessingStatus } from "@/types";
import { apiService } from "@/services/api";

const POLL_INTERVAL = 5000; // 5 seconds

function isActive(status: string | null): boolean {
  return status === "PENDING" || status === "PROCESSING";
}

export function useProcessingStatus(
  contentId: number | null,
  enabled = true
) {
  const [data, setData] = useState<ProcessingStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const dataRef = useRef<ProcessingStatus | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!contentId) return;
    try {
      setLoading(true);
      const status = await apiService.getProcessingStatus(contentId);
      dataRef.current = status;
      setData(status);
    } catch (err) {
      console.error("Failed to fetch processing status:", err);
    } finally {
      setLoading(false);
    }
  }, [contentId]);

  useEffect(() => {
    if (!contentId || !enabled) {
      setData(null);
      dataRef.current = null;
      return;
    }

    // Initial fetch
    fetchStatus();

    // Start polling
    timerRef.current = setInterval(() => {
      const current = dataRef.current;
      // Only poll if there's an active processing or no data yet
      if (
        current === null ||
        isActive(current.videoProcessingStatus) ||
        isActive(current.trailerProcessingStatus)
      ) {
        fetchStatus();
      }
    }, POLL_INTERVAL);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [contentId, enabled, fetchStatus]);

  return { data, loading, refetch: fetchStatus };
}
